import api from './authService';
import {
  PartnerDashboardSummary,
  PartnerProductListResponse,
  PaymentLinkListResponse,
  PaymentLinkResponse,
  RevenueReportResponse,
  OEMCustomerListResponse,
  OEMCustomerDetail,
  StripeConnectStatus,
  StripeConnectOnboardResponse,
  PriceValidationResult,
} from '../../types';

// ===========================================
// ダッシュボード
// ===========================================

export const getPartnerDashboard = async (): Promise<PartnerDashboardSummary> => {
  const response = await api.get<PartnerDashboardSummary>('/partner/dashboard');
  return response.data;
};

// ===========================================
// 商品管理
// ===========================================

export const getPartnerProducts = async (): Promise<PartnerProductListResponse> => {
  const response = await api.get<PartnerProductListResponse>('/partner/products');
  return response.data;
};

export const enableProduct = async (productId: number): Promise<{ success: boolean; message: string }> => {
  const response = await api.post(`/partner/products/${productId}/enable`);
  return response.data;
};

export const disableProduct = async (productId: number): Promise<{ success: boolean; message: string }> => {
  const response = await api.post(`/partner/products/${productId}/disable`);
  return response.data;
};

export interface PricingUpdate {
  [planCode: string]: {
    price: number;
    name?: string;
  };
}

export const updateProductPricing = async (
  productId: number,
  pricing: PricingUpdate
): Promise<{ success: boolean; message: string }> => {
  const response = await api.put(`/partner/products/${productId}/pricing`, { pricing });
  return response.data;
};

export const validatePrice = async (price: number): Promise<PriceValidationResult> => {
  const response = await api.post<PriceValidationResult>(`/partner/validate-price?price=${price}`);
  return response.data;
};

// ===========================================
// 決済リンク
// ===========================================

export const getPaymentLinks = async (): Promise<PaymentLinkListResponse> => {
  const response = await api.get<PaymentLinkListResponse>('/partner/payment-links');
  return response.data;
};

export const createPaymentLink = async (
  productId: number,
  planCode: string
): Promise<PaymentLinkResponse> => {
  const response = await api.post<PaymentLinkResponse>('/partner/payment-links', {
    product_id: productId,
    plan_code: planCode,
  });
  return response.data;
};

export const deactivatePaymentLink = async (
  linkId: number
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`/partner/payment-links/${linkId}`);
  return response.data;
};

// ===========================================
// 売上レポート
// ===========================================

export const getRevenueReport = async (
  year?: number,
  month?: number
): Promise<RevenueReportResponse> => {
  const params = new URLSearchParams();
  if (year) params.append('year', String(year));
  if (month) params.append('month', String(month));
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await api.get<RevenueReportResponse>(`/partner/revenue${query}`);
  return response.data;
};

// ===========================================
// 顧客管理
// ===========================================

export interface CustomerFilters {
  product_id?: number;
  plan_code?: string;
  status?: string;
  search?: string;
  skip?: number;
  limit?: number;
}

export const getCustomers = async (filters: CustomerFilters = {}): Promise<OEMCustomerListResponse> => {
  const params = new URLSearchParams();
  if (filters.product_id) params.append('product_id', String(filters.product_id));
  if (filters.plan_code) params.append('plan_code', filters.plan_code);
  if (filters.status) params.append('status', filters.status);
  if (filters.search) params.append('search', filters.search);
  if (filters.skip) params.append('skip', String(filters.skip));
  if (filters.limit) params.append('limit', String(filters.limit));
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await api.get<OEMCustomerListResponse>(`/partner/customers${query}`);
  return response.data;
};

export const getCustomerDetail = async (customerId: number): Promise<OEMCustomerDetail> => {
  const response = await api.get<OEMCustomerDetail>(`/partner/customers/${customerId}`);
  return response.data;
};

// ===========================================
// Stripe Connect
// ===========================================

export const getStripeConnectStatus = async (): Promise<StripeConnectStatus> => {
  const response = await api.get<StripeConnectStatus>('/partner/stripe/status');
  return response.data;
};

export const startStripeOnboarding = async (
  returnUrl: string,
  refreshUrl: string
): Promise<StripeConnectOnboardResponse> => {
  const response = await api.post<StripeConnectOnboardResponse>('/partner/stripe/onboard', {
    return_url: returnUrl,
    refresh_url: refreshUrl,
  });
  return response.data;
};

export const refreshStripeStatus = async (): Promise<{
  success: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  onboarding_complete: boolean;
}> => {
  const response = await api.post('/partner/stripe/refresh');
  return response.data;
};

// ===========================================
// ブランディング・ドメイン
// ===========================================

export interface BrandingSettings {
  branding: {
    logo_url?: string;
    favicon_url?: string;
    primary_color?: string;
    secondary_color?: string;
    brand_name?: string;
    support_email?: string;
    support_url?: string;
    terms_url?: string;
    privacy_url?: string;
  };
  custom_domain?: string;
  subdomain?: string;
  domain_verified: boolean;
}

export const getBranding = async (): Promise<BrandingSettings> => {
  const response = await api.get<BrandingSettings>('/partner/branding');
  return response.data;
};

export const updateBranding = async (
  branding: BrandingSettings['branding']
): Promise<{ success: boolean; branding: BrandingSettings['branding'] }> => {
  const response = await api.put('/partner/branding', branding);
  return response.data;
};

export const updateDomain = async (
  customDomain?: string,
  subdomain?: string
): Promise<{
  success: boolean;
  custom_domain: string | null;
  subdomain: string | null;
  domain_verified: boolean;
}> => {
  const response = await api.put('/partner/domain', {
    custom_domain: customDomain,
    subdomain: subdomain,
  });
  return response.data;
};
